import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import ClientSearchSelect from '../../components/ao/ClientSearchSelect';
import aoAPI from '../../api/aoAPI';

const fmt = (n) =>
  n ? new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2 }).format(n) : '0.00';

const AOPettyCashReport = () => {
  const [staffList, setStaffList] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString().substring(0, 10);
  const todayStr = today.toISOString().substring(0, 10);

  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(todayStr);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [groupBy, setGroupBy] = useState('staff'); // 'staff' | 'client'

  // Report data
  const [summary, setSummary] = useState({
    totalDebit: 0,
    totalCredit: 0,
    netBalance: 0,
    staffCount: 0,
  });
  const [groups, setGroups] = useState([]);

  // Load staff and clients
  useEffect(() => {
    const load = async () => {
      try {
        const [staffRes, clientRes] = await Promise.all([
          aoAPI.getStaff(),
          aoAPI.getClients(),
        ]);
        setStaffList(staffRes.data || []);
        setClients(clientRes.data || []);
      } catch (err) {
        console.error('Failed to load staff/clients', err);
      }
    };
    load();
  }, []);

  // Load ledger report
  const loadLedger = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aoAPI.getPettyCashLedger({
        dateFrom,
        dateTo,
        staffId: selectedStaff || undefined,
        clientId: selectedClient || undefined,
        groupBy,
      });

      if (res.data) {
        setSummary(res.data.summary || {});
        setGroups(res.data.groups || []);
      }
    } catch (err) {
      console.error('Failed to load ledger', err);
      setSummary({ totalDebit: 0, totalCredit: 0, netBalance: 0, staffCount: 0 });
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedStaff, selectedClient, groupBy]);

  useEffect(() => {
    if (dateFrom && dateTo) {
      loadLedger();
    }
  }, [loadLedger]);

  return (
    <Layout pageTitle="Petty Cash Report">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        
        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Date From */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            {/* Staff Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Staff</label>
              <select
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Staff</option>
                {staffList.map(s => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
            </div>

            {/* Client Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Client</label>
              <ClientSearchSelect
                clients={clients}
                value={selectedClient}
                onChange={setSelectedClient}
                placeholder="All Clients"
              />
            </div>
          </div>

          {/* Group By Toggle */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-700">Group By:</label>
            <div className="flex gap-2">
              <button
                onClick={() => setGroupBy('staff')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  groupBy === 'staff'
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-150 text-gray-700 border border-gray-300 hover:bg-gray-100'
                }`}
              >
                By Staff
              </button>
              <button
                onClick={() => setGroupBy('client')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  groupBy === 'client'
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-150 text-gray-700 border border-gray-300 hover:bg-gray-100'
                }`}
              >
                By Client
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-lg p-4">
            <p className="text-xs text-teal-600 font-semibold uppercase tracking-wide mb-1">Total Debits</p>
            <p className="text-2xl font-bold text-teal-700">₱{fmt(summary.totalDebit)}</p>
            <p className="text-xs text-teal-500 mt-1">Advances given</p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-lg p-4">
            <p className="text-xs text-red-600 font-semibold uppercase tracking-wide mb-1">Total Credits</p>
            <p className="text-2xl font-bold text-red-700">₱{fmt(summary.totalCredit)}</p>
            <p className="text-xs text-red-500 mt-1">Disbursements made</p>
          </div>

          <div className={`bg-gradient-to-br rounded-lg p-4 border ${
            summary.netBalance >= 0 
              ? 'from-green-50 to-green-100 border-green-200'
              : 'from-red-50 to-red-100 border-red-200'
          }`}>
            <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${
              summary.netBalance >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>Net Balance</p>
            <p className={`text-2xl font-bold ${
              summary.netBalance >= 0 ? 'text-green-700' : 'text-red-700'
            }`}>₱{fmt(Math.abs(summary.netBalance))}</p>
            <p className={`text-xs mt-1 ${
              summary.netBalance >= 0 ? 'text-green-500' : 'text-red-500'
            }`}>
              {summary.netBalance >= 0 ? 'Cash on hand' : 'Overspent'}
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
            <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-1">Staff Count</p>
            <p className="text-2xl font-bold text-blue-700">{summary.staffCount}</p>
            <p className="text-xs text-blue-500 mt-1">With activity</p>
          </div>
        </div>

        {/* Ledger Tables */}
        {!loading && groups.length > 0 && (
          <div className="space-y-6">
            {groups.map((group) => (
              <div key={group.groupId || 'unknown'} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Group Header */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-800 text-sm">{group.groupName || 'Unknown'}</h4>
                    <div className="flex gap-6 text-xs">
                      <span className="text-teal-700 font-semibold">
                        Debit ₱{fmt(group.totalDebit)}
                      </span>
                      <span className="text-red-600 font-semibold">
                        Credit ₱{fmt(group.totalCredit)}
                      </span>
                      <span className={`font-semibold ${
                        group.balance >= 0 ? 'text-green-700' : 'text-red-600'
                      }`}>
                        Balance ₱{fmt(Math.abs(group.balance))}
                        {group.balance < 0 && <span className="text-xs ml-1">(overspent)</span>}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Ledger Table */}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-6 py-2 text-left font-medium text-gray-600">Date</th>
                      <th className="px-6 py-2 text-left font-medium text-gray-600">Description</th>
                      <th className="px-6 py-2 text-right font-medium text-gray-600">Debit</th>
                      <th className="px-6 py-2 text-right font-medium text-gray-600">Credit</th>
                      <th className="px-6 py-2 text-right font-medium text-gray-600">Running Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.ledger.map((row, idx) => (
                      <tr 
                        key={`${group.groupId}-${idx}`} 
                        className="border-b border-gray-100 hover:bg-gray-50 text-xs"
                      >
                        <td className="px-6 py-3 text-gray-700">
                          {new Date(row.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-3 text-gray-700">
                          {row.description}
                        </td>
                        <td className="px-6 py-3 text-right font-mono">
                          {row.debit > 0 ? (
                            <span className="text-teal-700 font-medium">₱{fmt(row.debit)}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-right font-mono">
                          {row.credit > 0 ? (
                            <span className="text-red-600 font-medium">₱{fmt(row.credit)}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className={`px-6 py-3 text-right font-mono font-medium ${
                          row.runningBalance >= 0 ? 'text-green-700' : 'text-red-600'
                        }`}>
                          ₱{fmt(Math.abs(row.runningBalance))}
                          {row.runningBalance < 0 && <span className="text-xs ml-1">(overspent)</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {!loading && groups.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No petty cash activity for the selected period.
          </div>
        )}

        {loading && (
          <div className="text-center py-12 text-gray-500">
            <p>Loading report...</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AOPettyCashReport;
